import { useMemo } from "react";

import {
  FieldDefinition,
  FieldsOption,
  FilterFieldType,
} from "src/base-components/FilterPopover/types";
import {
  EventConfigEnriched,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import { FILTERABLE_TYPES } from "src/eventsCatalogue/filters/constants";
import { getEventProperties } from "src/eventsCatalogue/filters/utils";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { findEventSchema } from "src/eventsCatalogue/utils";

const DATATYPE_TO_FILTER_FIELD_TYPE: Partial<
  Record<PropertyDefinitionOutputTypeEnum, FilterFieldType>
> = {
  [PropertyDefinitionOutputTypeEnum.STRING]: "string",
  [PropertyDefinitionOutputTypeEnum.NUMBER]: "number",
  [PropertyDefinitionOutputTypeEnum.INTEGER]: "integer",
  [PropertyDefinitionOutputTypeEnum.BOOLEAN]: "boolean",
  [PropertyDefinitionOutputTypeEnum.ENUM]: "enum",
  [PropertyDefinitionOutputTypeEnum.TAGS]: "tags",
  [PropertyDefinitionOutputTypeEnum.DATE]: "date",
  [PropertyDefinitionOutputTypeEnum.DATETIME]: "datetime",
  [PropertyDefinitionOutputTypeEnum.URL]: "url",
  [PropertyDefinitionOutputTypeEnum.RELATION]: "relation",
};

type EventProperty = {
  name: string;
  displayName: string;
  datatype: PropertyDefinitionOutputTypeEnum;
  enumValues?: string[];
};

export const mapEventPropertyToFieldDefinition = (
  property: EventProperty,
): FieldDefinition => {
  const type = DATATYPE_TO_FILTER_FIELD_TYPE[property.datatype] ?? "string";

  const isEnumLike =
    property.datatype === PropertyDefinitionOutputTypeEnum.ENUM ||
    property.datatype === PropertyDefinitionOutputTypeEnum.TAGS;

  const options =
    isEnumLike && property.enumValues
      ? property.enumValues.map((v) => ({ key: v, value: v }))
      : undefined;

  return {
    key: property.name,
    value: property.displayName,
    type,
    ...(options ? { options } : {}),
  };
};

const isTimestampField = (name: string): boolean =>
  name.toLowerCase().includes("timestamp");

const getEnumValues = (
  currentEvent: EventConfigEnriched | undefined,
  propertyName: string,
): string[] | undefined =>
  currentEvent?.properties?.[propertyName]?.enum_schema?.values?.map(
    (v) => v.value,
  );

export const useEventFilterFields = (
  eventType: string,
): { fields: FieldsOption[]; isLoading: boolean } => {
  const { data: eventSchemas, isLoading } = useEventSchemas();

  const fields = useMemo<FieldsOption[]>(() => {
    const currentEvent = findEventSchema(eventType, eventSchemas);
    const properties = getEventProperties(currentEvent);

    return properties
      .filter(
        (property) =>
          FILTERABLE_TYPES.includes(property.datatype) &&
          !isTimestampField(property.name),
      )
      .map((property) =>
        mapEventPropertyToFieldDefinition({
          ...property,
          enumValues: getEnumValues(currentEvent, property.name),
        }),
      );
  }, [eventType, eventSchemas]);

  return { fields, isLoading };
};
